import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Trophy, CheckCircle } from 'lucide-react';
import { SubscriptionPlan } from '../../types/subscription';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface Props {
  isOpen: boolean;
  lead: { id: string; name: string; email?: string } | null;
  onSkip: () => void;       // move to WON without subscription
  onConfirm: (planId: string, startDate: string) => void; // move to WON + create subscription
  onClose: () => void;
}

function formatPrice(paise: number, cycle: string): string {
  const amount = (paise / 100).toLocaleString('en-IN');
  const suffix = cycle === 'MONTHLY' ? '/mo' : cycle === 'QUARTERLY' ? '/qtr' : '/yr';
  return `₹${amount}${suffix}`;
}

export const WonSubscriptionModal: React.FC<Props> = ({ isOpen, lead, onSkip, onConfirm, onClose }) => {
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split('T')[0]);

  const { data: plans = [], isLoading: plansLoading } = useQuery<SubscriptionPlan[]>({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscription-plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch plans');
      const result = await response.json();
      return (result.data || []).filter((p: SubscriptionPlan) => p.isActive);
    },
    enabled: isOpen,
  });

  const selectedPlan = useMemo(
    () => plans.find(p => p.id === selectedPlanId) || null,
    [plans, selectedPlanId]
  );

  if (!isOpen || !lead) return null;

  const handleConfirm = () => {
    if (!selectedPlanId) {
      alert('Please select a subscription plan, or click Skip to proceed without one.');
      return;
    }
    onConfirm(selectedPlanId, new Date(startDate).toISOString());
  };

  const handleSkip = () => {
    onSkip();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <Trophy className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Mark as WON</h2>
            <p className="text-sm text-gray-500">{lead.name}</p>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Assign a subscription plan to this deal, or skip to mark as WON without one.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subscription Plan
            </label>
            {plansLoading ? (
              <div className="text-sm text-gray-500">Loading plans...</div>
            ) : (
              <select
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a plan (optional)</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} — ₹{(plan.price / 100).toLocaleString('en-IN')}/{plan.billingCycle === 'MONTHLY' ? 'mo' : plan.billingCycle === 'QUARTERLY' ? 'qtr' : 'yr'}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Plan price comparison */}
          {!selectedPlanId && plans.length > 1 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-xs font-medium text-gray-500 mb-2">Available Plans</p>
              <div className="space-y-1.5">
                {plans.map(plan => (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlanId(plan.id)}
                    className="w-full flex items-center justify-between text-left px-2 py-1.5 rounded hover:bg-gray-100 transition-colors"
                  >
                    <span className="text-sm text-gray-700">{plan.name}</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatPrice(plan.price, plan.billingCycle)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Selected plan features */}
          {selectedPlan && selectedPlan.features && selectedPlan.features.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-xs font-medium text-green-700 mb-2">Plan Features</p>
              <ul className="space-y-1">
                {selectedPlan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-green-800">
                    <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-green-500" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {selectedPlanId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subscription Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}

          {/* Confirmation summary */}
          {selectedPlanId && selectedPlan && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-medium text-blue-700 mb-2">What happens next</p>
              <ul className="space-y-1.5 text-sm text-blue-800">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-0.5">1.</span>
                  A subscription to <span className="font-medium">{selectedPlan.name}</span> will be created for{' '}
                  <span className="font-medium">{lead.name}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-0.5">2.</span>
                  Deliverables will be auto-generated from the plan template
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-0.5">3.</span>
                  Portal access will be auto-granted to{' '}
                  <span className="font-medium">{lead.email || 'the client'}</span>
                </li>
              </ul>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={handleSkip}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Skip — assign later
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedPlanId}
            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
          >
            Confirm WON
          </button>
        </div>
      </div>
    </div>
  );
};
